import { ReportExportButtons } from '../components/ReportExportButtons'
import { useCallback, useEffect, useMemo, useState, type FormEvent, type ReactNode } from 'react'
import { Button, ButtonGroup, ButtonLink, Callout, Card, CardContent, CardGrid, CardGridItem, CardHeader, DataTable, DataTableEmptyRow, Dialog, EmptyState, FilterBar, FormGrid, FormSection, IconButton, LoadingState, Page, PageHeader, Select, StatCard as UiStatCard, StatGrid, StatusPill, Tabs, TextField, useConfirm, type PillTone } from '../components/ui'
import { type LoginResponse } from '../api/auth'
import { departmentsApi } from '../api/team'
import { ExtendedDimensionsTab } from '../components/ExtendedDimensionsTab'
import { useFinanceDimensions } from '../lib/financeDimensions'
import { suppliersApi, type SupplierOption } from '../api/finance-ext'
import {
  BUDGET_DIMENSIONS,
  BUDGET_DIMENSIONS_EXTENDED,
  ALLOCATION_METHODS,
  BUDGET_PERIOD_KINDS,
  COST_DIMENSIONS,
  EMPTY_DIMENSION_OPTIONS,
  RECURRING_FREQUENCIES,
  budgetsApi,
  categoryHierarchyApi,
  costCategoriesApi,
  costAllocationApi,
  costCentresApi,
  costsApi,
  variableCostApi,
  VARIABLE_COST_BASES,
  loadDimensionOptions,
  recurringExpensesApi,
  type AllocationMethod,
  type AllocationPreview,
  type AllocationReport,
  type AllocationRule,
  type AllocationRuleBody,
  type AllocationTarget,
  type BudgetBody,
  type BudgetVariance,
  type CostByDimension,
  type CostDimensionId,
  type DimensionOptions,
  type CostCategory,
  type CostCategoryNode,
  type CostCentre,
  type CostSchedule,
  type CostsDashboard,
  type RecurringExpense,
  type RecurringExpenseBody,
  type VariableCostBasis,
  type VariableCostMonth,
  type VariableCostReport,
  type VariableCostRule,
  type VariableCostRuleBody,
} from '../api/costs'

/**
 * F-064 (TASK-0318, REQ-148-001 / REQ-146-001 / REQ-151-001 / REQ-152-001 / REQ-169-001 /
 * REQ-176-001 / REQ-177-003 / REQ-187-001) — controllo dei costi: dashboard costi, scadenziario delle
 * uscite, budget di costo, spese ricorrenti, centri di costo e piano gestionale (gerarchia delle
 * categorie).
 *
 * <b>Nessun calcolo qui.</b> Totali, consuntivo, scostamento (assoluto e %), alert e somme per
 * categoria arrivano dal server; la pagina li mostra. Sono mostrati solo costi: nessun ricavo, margine
 * o ROI. Tutti gli endpoint usati per scrivere e la dashboard sono Admin lato server (qui la pagina e
 * il menu sono comunque riservati all'Admin, ma la protezione reale è server-side).
 */

interface Props {
  auth: LoginResponse
}

type Tab = 'dashboard' | 'dimension' | 'schedule' | 'budget' | 'recurring' | 'centres' | 'allocation' | 'variable' | 'dimensions'

const TABS: { id: Tab; label: string }[] = [
  { id: 'dashboard', label: 'Dashboard costi' },
  { id: 'dimension', label: 'Per dimensione' },
  { id: 'schedule', label: 'Scadenziario' },
  { id: 'budget', label: 'Budget' },
  { id: 'recurring', label: 'Spese ricorrenti' },
  { id: 'centres', label: 'Centri di costo' },
  { id: 'allocation', label: 'Ripartizione' },
  { id: 'variable', label: 'Costi variabili' },
]

/** R3-10 (flag finanza.dimensioni_estese): scheda con le liste BU e sede, l'organico dei centri e le categorie di ricavo. */
const EXTENDED_TAB: { id: Tab; label: string } = { id: 'dimensions', label: 'Dimensioni e piano dei conti' }

function formatEur(n: number): string {
  return '€ ' + n.toLocaleString('it-IT', { minimumFractionDigits: 2, maximumFractionDigits: 2 })
}

function formatDate(s?: string | null): string {
  if (!s) return '—'
  return new Date(s).toLocaleDateString('it-IT')
}

function formatPct(p?: number | null): string {
  if (p === null || p === undefined) return '—'
  return (p > 0 ? '+' : '') + (p * 100).toLocaleString('it-IT', { maximumFractionDigits: 2 }) + '%'
}

function signedEur(n: number): string {
  return (n > 0 ? '+' : n < 0 ? '−' : '') + formatEur(Math.abs(n))
}

function errMsg(e: unknown, fallback: string): string {
  return e instanceof Error ? e.message : fallback
}

function periodLabel(b: { periodKind: string; periodIndex?: number | null; year: number }): string {
  if (b.periodKind === 'Annuale') return `Anno ${b.year}`
  if (b.periodKind === 'Trimestrale') return `${b.periodIndex}° trimestre ${b.year}`
  return `${String(b.periodIndex).padStart(2, '0')}/${b.year}`
}

function monthStart(): string {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth(), 1, 12).toISOString().slice(0, 10)
}

function monthEnd(): string {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth() + 1, 0, 12).toISOString().slice(0, 10)
}

export function CostControlPage({ auth }: Props) {
  // F-069: Amministrazione scrive come l'Admin; Commercialista legge soltanto (il server lo impone).
  const writer = auth.role === 'Administrator' || auth.role === 'EqualAdmin' || auth.role === 'EqualAmministrazione'
  const admin = writer || auth.role === 'EqualCommercialista'
  const [tab, setTab] = useState<Tab>('dashboard')
  const finDims = useFinanceDimensions()

  // Anagrafiche condivise fra le schede.
  const [centres, setCentres] = useState<CostCentre[]>([])
  const [categories, setCategories] = useState<CostCategory[]>([])
  const [departments, setDepartments] = useState<{ id: number; name: string }[]>([])
  const [suppliers, setSuppliers] = useState<SupplierOption[]>([])
  const [refError, setRefError] = useState('')

  const loadRefs = useCallback(async () => {
    try {
      const [c, cat] = await Promise.all([costCentresApi.list(true), costCategoriesApi.list(true)])
      setCentres(c)
      setCategories(cat)
      setRefError('')
    } catch (e) {
      setRefError(errMsg(e, 'Errore durante il caricamento delle anagrafiche'))
    }
    departmentsApi.list().then(d => setDepartments(d.map(x => ({ id: x.id, name: x.name })))).catch(() => setDepartments([]))
    suppliersApi.list().then(setSuppliers).catch(() => setSuppliers([]))
  }, [])

  useEffect(() => { if (admin) void loadRefs() }, [admin, loadRefs])

  if (!admin) {
    return (
      <Page>
        <Callout tone="warning">Il controllo costi è riservato all'amministrazione.</Callout>
      </Page>
    )
  }

  const centreName = (id?: number | null) => (id ? centres.find(c => c.id === id)?.name ?? `#${id}` : '—')
  const categoryName = (id?: number | null) => (id ? categories.find(c => c.id === id)?.name ?? `#${id}` : '—')
  const departmentName = (id?: number | null) => (id ? departments.find(d => d.id === id)?.name ?? `Settore #${id}` : 'Non assegnato')

  return (
    <Page>
      <PageHeader
        title="Controllo costi"
        icon="price_check"
        actions={(
          <>
            {/* F-085: scadenziario unico di pagamenti e incassi. */}
            <ButtonLink to="/finance/calendar" variant="outlined">Scadenziario unico</ButtonLink>
            <ReportExportButtons title="Controllo costi" />
          </>
        )}
      />
      {!writer && (
        <Callout tone="info">Sola lettura: le modifiche richiedono il ruolo Amministrazione.</Callout>
      )}

      <Tabs
        aria-label="Sezioni del controllo costi"
        items={(finDims.enabled ? [...TABS, EXTENDED_TAB] : TABS).map(t => ({ id: t.id, label: t.label }))}
        value={tab}
        onChange={id => setTab(id as Tab)}
      />

      {refError && <Callout tone="error">{refError}</Callout>}

      {tab === 'dashboard' && <DashboardTab departmentName={departmentName} />}
      {tab === 'dimension' && <DimensionTab />}
      {tab === 'schedule' && <ScheduleTab categoryName={categoryName} centreName={centreName} />}
      {tab === 'budget' && (
        <BudgetTab
          centres={centres}
          categories={categories}
          departments={departments}
          centreName={centreName}
          categoryName={categoryName}
          departmentName={departmentName}
        />
      )}
      {tab === 'recurring' && (
        <RecurringTab
          centres={centres}
          categories={categories}
          suppliers={suppliers}
          centreName={centreName}
          categoryName={categoryName}
        />
      )}
      {tab === 'allocation' && (
        <AllocationTab centres={centres} categories={categories} centreName={centreName} writer={writer} />
      )}
      {tab === 'variable' && (
        <VariableCostTab
          centres={centres}
          categories={categories}
          departments={departments}
          suppliers={suppliers}
          centreName={centreName}
          categoryName={categoryName}
          writer={writer}
        />
      )}
      {tab === 'dimensions' && finDims.enabled && (
        <ExtendedDimensionsTab centres={centres} writer={writer} onChanged={loadRefs} />
      )}
      {tab === 'centres' && (
        <CentresTab
          centres={centres}
          categories={categories}
          departments={departments}
          departmentName={departmentName}
          onChanged={loadRefs}
        />
      )}
    </Page>
  )
}

/* ── Per dimensione (F-092) ──────────────────────────────────────────────────────────────────── */

/**
 * F-092 / REQ-149-001 — i costi del periodo raggruppati per operatore, prodotto, campagna, gara, agente o progetto.
 * Totali e raggruppamento arrivano dal server (stesse spese della dashboard: le annullate sono escluse); qui si
 * risolvono soltanto i nomi con le liste esistenti. Le spese senza la dimensione restano visibili come «Non
 * assegnato», così la somma delle righe è sempre il totale del periodo.
 */
function DimensionTab() {
  const [dimension, setDimension] = useState<CostDimensionId>('operator')
  const [from, setFrom] = useState(monthStart())
  const [to, setTo] = useState(monthEnd())
  const [data, setData] = useState<CostByDimension | null>(null)
  const [options, setOptions] = useState<DimensionOptions>(EMPTY_DIMENSION_OPTIONS)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    loadDimensionOptions().then(setOptions).catch(() => setOptions(EMPTY_DIMENSION_OPTIONS))
  }, [])

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setData(await costsApi.byDimension(dimension, from || undefined, to || undefined))
    } catch (e) {
      setData(null)
      setError(errMsg(e, 'Errore durante il caricamento dei costi per dimensione'))
    } finally {
      setLoading(false)
    }
  }, [dimension, from, to])

  useEffect(() => { void load() }, [load])

  const nameOf = (key: string | null): string => {
    if (key === null) return 'Non assegnato'
    if (dimension === 'project') return key
    return options[dimension].find(o => String(o.id) === key)?.label ?? `#${key}`
  }

  return (
    <>
      <FilterBar>
        <div>
          <Select label="Dimensione" value={dimension} onChange={e => setDimension(e.target.value as CostDimensionId)}>
            {COST_DIMENSIONS.map(d => <option key={d.id} value={d.id}>{d.label}</option>)}
          </Select>
        </div>
        <div>
          <TextField label="Dal" type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div>
          <TextField label="Al" type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}
      {loading && !data && <LoadingState />}

      {data && (
        <Card variant="outlined" padding="none">
          <CardHeader
            title={`Costi per ${COST_DIMENSIONS.find(d => d.id === data.dimension)?.label.toLowerCase()} · ${formatDate(data.from)} – ${formatDate(data.to)}`}
            subtitle={`${data.totals.count} spese · totale ${formatEur(data.totals.total)} · netto ${formatEur(data.totals.net)}`}
          />
          {data.items.length === 0 ? (
            <EmptyState icon="inbox" title="Nessuna spesa nel periodo." />
          ) : (
            <DataTable variant="plain">
                <thead>
                  <tr>
                    <th>{COST_DIMENSIONS.find(d => d.id === data.dimension)?.label}</th>
                    <th className="text-end">Spese</th>
                    <th className="text-end">Netto</th>
                    <th className="text-end">Totale</th>
                    <th className="text-end">% sul totale</th>
                  </tr>
                </thead>
                <tbody>
                  {data.items.map(i => (
                    <tr key={i.key ?? '__none'} className={i.assigned ? '' : 'text-muted'}>
                      <td>{nameOf(i.key)}</td>
                      <td className="text-end md-num">{i.count}</td>
                      <td className="text-end text-nowrap md-num">{formatEur(i.net)}</td>
                      <td className="text-end text-nowrap md-typescale-emphasized-body-medium md-num">{formatEur(i.total)}</td>
                      <td className="text-end md-num">
                        {data.totals.total > 0 ? `${((i.total / data.totals.total) * 100).toLocaleString('it-IT', { maximumFractionDigits: 1 })}%` : '—'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
          )}
        </Card>
      )}
    </>
  )
}

/* ── Dashboard costi ─────────────────────────────────────────────────────────────────────────── */

function flatten(nodes: CostCategoryNode[], depth = 0): { node: CostCategoryNode; depth: number }[] {
  return nodes.flatMap(n => [{ node: n, depth }, ...flatten(n.children, depth + 1)])
}

function StatCard({ label, value, note }: { label: string; value: string; note?: string }) {
  return (
    <UiStatCard label={label} value={value} supporting={note} />
  )
}

function DashboardTab({ departmentName }: { departmentName: (id?: number | null) => string }) {
  const [from, setFrom] = useState(monthStart())
  const [to, setTo] = useState(monthEnd())
  const [data, setData] = useState<CostsDashboard | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      setData(await costsApi.dashboard(from || undefined, to || undefined))
    } catch (e) {
      setData(null)
      setError(errMsg(e, 'Errore durante il caricamento della dashboard costi'))
    } finally {
      setLoading(false)
    }
  }, [from, to])

  useEffect(() => { void load() }, [load])

  const rows = useMemo(() => (data ? flatten(data.byCategory) : []), [data])

  return (
    <>
      <FilterBar>
        <div>
          <TextField label="Dal" type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div>
          <TextField label="Al" type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
      </FilterBar>

      {error && <Callout tone="error">{error}</Callout>}
      {loading && !data && <LoadingState />}

      {data && (
        <>
          {data.alerts.length > 0 && (
            <Callout tone="warning">
              <strong>Budget superati oltre la soglia ({(data.threshold * 100).toLocaleString('it-IT')}%):</strong>
              <ul className="mb-0">
                {data.alerts.map(a => (
                  <li key={a.id}>{periodLabel(a)} · {a.dimension}{a.dimensionId ? ` #${a.dimensionId}` : ''}: {signedEur(a.variance)} ({formatPct(a.variancePct)})</li>
                ))}
              </ul>
            </Callout>
          )}

          <StatGrid>
            <StatCard label="Costi del periodo" value={formatEur(data.totals.total)} note={`${data.totals.count} spese · netto ${formatEur(data.totals.net)}`} />
            <StatCard label="Ricorrenti" value={formatEur(data.totals.recurring)} note={`non ricorrenti ${formatEur(data.totals.nonRecurring)}`} />
            <StatCard label="Mese" value={formatEur(data.month.total)} note={`${formatDate(data.month.from)} – ${formatDate(data.month.to)}`} />
            <StatCard label="Anno" value={formatEur(data.year.total)} note={`${formatDate(data.year.from)} – ${formatDate(data.year.to)}`} />
            <StatCard
              label="Periodo precedente"
              value={formatEur(data.previousPeriod.total)}
              note={`${formatDate(data.previousPeriod.from)} – ${formatDate(data.previousPeriod.to)} · ${signedEur(data.previousPeriod.delta)} (${formatPct(data.previousPeriod.deltaPct)})`}
            />
          </StatGrid>

          <CardGrid min={360}>
              <Card variant="outlined" padding="none">
                <CardHeader title="Per categoria (piano gestionale)" />
                <DataTable variant="plain">
                    <thead><tr><th>Categoria</th><th className="text-end">Proprio</th><th className="text-end">Totale</th></tr></thead>
                    <tbody>
                      {rows.length === 0 && <DataTableEmptyRow colSpan={3} title="Nessuna categoria" />}
                      {rows.map(({ node, depth }) => (
                        <tr key={node.id}>
                          <td style={{ paddingLeft: 12 + depth * 20 }}>{node.name}</td>
                          <td className="text-end text-nowrap md-num">{formatEur(node.ownTotal)}</td>
                          <td className="text-end text-nowrap md-typescale-emphasized-body-medium md-num">{formatEur(node.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
              </Card>

              <Card variant="outlined" padding="none">
                <CardHeader title="Per centro di costo" />
                <DataTable variant="plain">
                    <tbody>
                      {data.byCostCentre.length === 0 && <DataTableEmptyRow colSpan={2} title="Nessuna spesa" />}
                      {data.byCostCentre.map((c, i) => (
                        <tr key={c.costCentreId ?? `n${i}`}>
                          <td>{c.costCentreId ? `${c.code} · ${c.name}` : 'Senza centro di costo'}</td>
                          <td className="text-end text-nowrap md-num">{formatEur(c.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
              </Card>
              <Card variant="outlined" padding="none">
                <CardHeader title="Per settore" />
                <DataTable variant="plain">
                    <tbody>
                      {data.bySector.length === 0 && <DataTableEmptyRow colSpan={2} title="Nessuna spesa" />}
                      {data.bySector.map((s, i) => (
                        <tr key={s.departmentId ?? `n${i}`}>
                          <td>{departmentName(s.departmentId)}</td>
                          <td className="text-end text-nowrap md-num">{formatEur(s.total)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </DataTable>
              </Card>

              <CardGridItem span="full">
                <BudgetTable
                  title="Budget del periodo (consuntivo e scostamento)"
                  items={data.budgets}
                  label={() => ''}
                  empty="Nessun budget per questo periodo"
                />
              </CardGridItem>
          </CardGrid>
        </>
      )}
    </>
  )
}

function BudgetTable({ title, items, label, empty, onRemove }: {
  title: string
  items: BudgetVariance[]
  label: (b: BudgetVariance) => string
  empty: string
  onRemove?: (b: BudgetVariance) => void
}) {
  return (
    <Card variant="outlined" padding="none">
      <CardHeader title={title} />
      <DataTable variant="plain">
          <thead>
            <tr>
              <th>Periodo</th><th>Dimensione</th>
              <th className="text-end">Budget</th><th className="text-end">Consuntivo</th>
              <th className="text-end">Scostamento</th><th className="text-end">%</th><th />
              {onRemove && <th />}
            </tr>
          </thead>
          <tbody>
            {items.length === 0 && <DataTableEmptyRow colSpan={8} title={empty} />}
            {items.map(b => (
              <tr key={b.id} style={b.alert ? { background: 'hsl(var(--md-warning-container))' } : undefined}>
                <td className="text-nowrap">{periodLabel(b)}</td>
                <td>{label(b) || `${b.dimension}${b.dimensionId ? ` #${b.dimensionId}` : ''}`}</td>
                <td className="text-end text-nowrap md-num">{formatEur(b.budget)}</td>
                <td className="text-end text-nowrap md-num">{formatEur(b.actual)}</td>
                <td className={`text-end text-nowrap ${b.variance > 0 ? 'text-danger' : 'text-success'}`}>{signedEur(b.variance)}</td>
                <td className={`text-end text-nowrap ${b.variance > 0 ? 'text-danger' : 'text-success'}`}>{formatPct(b.variancePct)}</td>
                <td>{b.alert && <StatusPill tone="warning">Oltre soglia</StatusPill>}</td>
                {onRemove && (
                  <td className="text-end">
                    <IconButton icon="delete" size="sm" aria-label="Elimina" onClick={() => onRemove(b)} />
                  </td>
                )}
              </tr>
            ))}
          </tbody>
        </DataTable>
    </Card>
  )
}

/* ── Scadenziario ────────────────────────────────────────────────────────────────────────────── */

function ScheduleTab({ categoryName, centreName }: {
  categoryName: (id?: number | null) => string
  centreName: (id?: number | null) => string
}) {
  const [days, setDays] = useState<7 | 30 | 60 | 90>(30)
  const [data, setData] = useState<CostSchedule | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    setError('')
    costsApi.schedule(days)
      .then(d => { if (!cancelled) setData(d) })
      .catch(e => { if (!cancelled) { setData(null); setError(errMsg(e, 'Errore durante il caricamento dello scadenziario')) } })
    return () => { cancelled = true }
  }, [days])

  function Table({ items, title }: { items: CostSchedule['items']; title: string }) {
    return (
      <Card variant="outlined" padding="none">
        <CardHeader title={title} />
        <DataTable variant="plain">
            <thead><tr><th>Scadenza</th><th>Descrizione</th><th>Categoria</th><th>Centro di costo</th><th>Stato</th><th className="text-end">Importo</th></tr></thead>
            <tbody>
              {items.length === 0 && <DataTableEmptyRow colSpan={6} title="Nessuna uscita" />}
              {items.map(i => (
                <tr key={i.id}>
                  <td className="text-nowrap">{formatDate(i.dueDate)}</td>
                  <td>{i.description}{i.recurring && <StatusPill tone="info" className="ms-2">ricorrente</StatusPill>}</td>
                  <td>{categoryName(i.expenseCategoryId)}</td>
                  <td>{centreName(i.costCentreId)}</td>
                  <td>{i.status}</td>
                  <td className="text-end text-nowrap md-num">{formatEur(i.totalAmount)}</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
      </Card>
    )
  }

  return (
    <>
      <FilterBar>
        <span className="md-typescale-body-small text-muted">Uscite in scadenza nei prossimi</span>
        <ButtonGroup
          aria-label="Uscite in scadenza nei prossimi giorni"
          size="sm"
          options={([7, 30, 60, 90] as const).map(d => ({ value: String(d), label: `${d} giorni` }))}
          value={String(days)}
          onChange={v => setDays(Number(v) as 7 | 30 | 60 | 90)}
        />
      </FilterBar>
      {error && <Callout tone="error">{error}</Callout>}
      {data && (
        <>
          <StatGrid>
            <StatCard label="Da pagare" value={formatEur(data.total)} note={`Entro il ${formatDate(data.until)} · spese non pagate né annullate`} />
            <StatCard label="Scadute non pagate" value={formatEur(data.overdueTotal)} />
          </StatGrid>
          {data.overdue.length > 0 && <Table items={data.overdue} title="Scadute" />}
          <Table items={data.items} title="In scadenza" />
        </>
      )}
    </>
  )
}

/* ── Budget ──────────────────────────────────────────────────────────────────────────────────── */

function BudgetTab({ centres, categories, departments, centreName, categoryName, departmentName }: {
  centres: CostCentre[]
  categories: CostCategory[]
  departments: { id: number; name: string }[]
  centreName: (id?: number | null) => string
  categoryName: (id?: number | null) => string
  departmentName: (id?: number | null) => string
}) {
  const confirm = useConfirm()
  const [year, setYear] = useState(new Date().getFullYear())
  const [items, setItems] = useState<BudgetVariance[]>([])
  const [threshold, setThreshold] = useState(0.2)
  const [error, setError] = useState('')
  const [showForm, setShowForm] = useState(false)
  // R3-10: budget per team e campagna (solo con il flag finanza.dimensioni_estese).
  const finDims = useFinanceDimensions()
  const [campaigns, setCampaigns] = useState<DimensionOptions['campaign']>([])
  useEffect(() => {
    if (finDims.enabled) void loadDimensionOptions().then(o => setCampaigns(o.campaign)).catch(() => setCampaigns([]))
  }, [finDims.enabled])

  const load = useCallback(async () => {
    setError('')
    try {
      const r = await budgetsApi.variance(year)
      setItems(r.items)
      setThreshold(r.threshold)
    } catch (e) {
      setItems([])
      setError(errMsg(e, 'Errore durante il caricamento dei budget'))
    }
  }, [year])

  useEffect(() => { void load() }, [load])

  const label = (b: BudgetVariance) => {
    switch (b.dimension) {
      case 'Totale': return 'Totale costi'
      case 'CentroDiCosto': return `Centro: ${centreName(b.dimensionId)}`
      case 'Categoria': return `Categoria: ${categoryName(b.dimensionId)}`
      case 'Team': return `Team: ${finDims.label('team', b.dimensionId) ?? '—'}`
      case 'Campagna': return `Campagna: ${campaigns.find(c => c.id === b.dimensionId)?.label ?? `#${b.dimensionId}`}`
      default: return `Settore: ${departmentName(b.dimensionId)}`
    }
  }

  async function remove(b: BudgetVariance) {
    if (!(await confirm({ title: 'Elimina budget', message: 'Eliminare questo budget?', confirmLabel: 'Elimina', destructive: true }))) return
    try {
      await budgetsApi.remove(b.id)
      void load()
    } catch (e) {
      setError(errMsg(e, 'Errore durante l\'eliminazione'))
    }
  }

  return (
    <>
      <FilterBar>
        <TextField label="Anno" type="number" className="md-u-w-100" value={year} onChange={e => setYear(Number(e.target.value))} />
      </FilterBar>
      <div className="d-flex gap-3 align-items-center flex-wrap mb-3">
        <Button variant="filled" type="button" onClick={() => setShowForm(true)}>Nuovo budget</Button>
        <span className="md-typescale-body-small text-muted">Allerta oltre {(threshold * 100).toLocaleString('it-IT')}% di scostamento</span>
      </div>
      {error && <Callout tone="error">{error}</Callout>}
      <BudgetTable title={`Budget ${year}`} items={items} label={label} empty="Nessun budget per questo anno" onRemove={b => void remove(b)} />
      {showForm && (
        <BudgetModal
          year={year}
          centres={centres}
          categories={categories}
          departments={departments}
          extended={finDims.enabled}
          teams={finDims.teams}
          campaigns={campaigns}
          onClose={() => setShowForm(false)}
          onSaved={() => { setShowForm(false); void load() }}
        />
      )}
    </>
  )
}

function BudgetModal({ year, centres, categories, departments, extended, teams, campaigns, onClose, onSaved }: {
  year: number
  centres: CostCentre[]
  categories: CostCategory[]
  departments: { id: number; name: string }[]
  extended: boolean
  teams: { id: number; label: string }[]
  campaigns: { id: number; label: string }[]
  onClose: () => void
  onSaved: () => void
}) {
  const [kind, setKind] = useState<string>('Mensile')
  const [index, setIndex] = useState('1')
  const [dimension, setDimension] = useState<string>('Totale')
  const [dimensionId, setDimensionId] = useState('')
  const [amount, setAmount] = useState('')
  const [notes, setNotes] = useState('')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function save() {
    const value = Number(amount.replace(',', '.'))
    if (!Number.isFinite(value) || value <= 0) { setError('Importo non valido'); return }
    if (dimension !== 'Totale' && !dimensionId) { setError('Scegli la dimensione'); return }
    const body: BudgetBody = {
      year,
      periodKind: kind,
      periodIndex: kind === 'Annuale' ? undefined : Number(index),
      dimension,
      dimensionId: dimension === 'Totale' ? undefined : Number(dimensionId),
      amount: value,
      notes: notes.trim() || undefined,
    }
    setSaving(true)
    setError('')
    try {
      await budgetsApi.create(body)
      onSaved()
    } catch (e) {
      setError(errMsg(e, 'Errore durante il salvataggio'))
    } finally {
      setSaving(false)
    }
  }

  const maxIndex = kind === 'Trimestrale' ? 4 : 12

  return (
    <Modal title={`Nuovo budget di costo ${year}`} onClose={onClose} onSave={() => void save()} saving={saving}>
      {error && <Callout tone="error">{error}</Callout>}
      <FormGrid>
        <Select label="Periodo" value={kind} onChange={e => { setKind(e.target.value); setIndex('1') }}>
          {BUDGET_PERIOD_KINDS.map(k => <option key={k} value={k}>{k}</option>)}
        </Select>
        {kind !== 'Annuale' && (
          <Select label={kind === 'Trimestrale' ? 'Trimestre' : 'Mese'} value={index} onChange={e => setIndex(e.target.value)}>
            {Array.from({ length: maxIndex }, (_, i) => i + 1).map(i => <option key={i} value={i}>{i}</option>)}
          </Select>
        )}
        <Select label="Dimensione" value={dimension} onChange={e => { setDimension(e.target.value); setDimensionId('') }}>
          {(extended ? BUDGET_DIMENSIONS_EXTENDED : BUDGET_DIMENSIONS).map(d => <option key={d} value={d}>{d === 'CentroDiCosto' ? 'Centro di costo' : d}</option>)}
        </Select>
        {dimension !== 'Totale' && (
          <Select label={dimension === 'CentroDiCosto' ? 'Centro di costo' : dimension} value={dimensionId} onChange={e => setDimensionId(e.target.value)}>
            <option value="">— scegli —</option>
            {dimension === 'CentroDiCosto' && centres.filter(c => c.isActive).map(c => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
            {dimension === 'Categoria' && categories.filter(c => c.isActive).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
            {dimension === 'Settore' && departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
            {dimension === 'Team' && teams.map(t => <option key={t.id} value={t.id}>{t.label}</option>)}
            {dimension === 'Campagna' && campaigns.map(c => <option key={c.id} value={c.id}>{c.label}</option>)}
          </Select>
        )}
        <TextField label="Importo budget (€)" inputMode="decimal" value={amount} onChange={e => setAmount(e.target.value)} />
        <TextField className="md-span-full" label="Note" value={notes} onChange={e => setNotes(e.target.value)} />
      </FormGrid>
    </Modal>
  )
}

/* ── Spese ricorrenti ────────────────────────────────────────────────────────────────────────── */

function RecurringTab({ centres, categories, suppliers, centreName, categoryName }: {
  centres: CostCentre[]
  categories: CostCategory[]
  suppliers: SupplierOption[]
  centreName: (id?: number | null) => string
  categoryName: (id?: number | null) => string
}) {
  const confirm = useConfirm()
  const [items, setItems] = useState<RecurringExpense[]>([])
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [editing, setEditing] = useState<RecurringExpense | 'new' | null>(null)

  const load = useCallback(async () => {
    try {
      setItems(await recurringExpensesApi.list(true))
      setError('')
    } catch (e) {
      setItems([])
      setError(errMsg(e, 'Errore durante il caricamento delle spese ricorrenti'))
    }
  }, [])

  useEffect(() => { void load() }, [load])

  async function generate(r: RecurringExpense) {
    setMessage('')
    setError('')
    try {
      const res = await recurringExpensesApi.generate(r.id)
      setMessage(`Create ${res.created} spese Previste fino al ${formatDate(res.until)}`)
    } catch (e) {
      setError(errMsg(e, 'Errore durante la generazione'))
    }
  }

  async function deactivate(r: RecurringExpense) {
    if (!(await confirm({ title: 'Disattiva ricorrenza', message: 'Disattivare la ricorrenza? Le spese già create restano.', confirmLabel: 'Disattiva', destructive: true }))) return
    try {
      await recurringExpensesApi.deactivate(r.id)
      void load()
    } catch (e) {
      setError(errMsg(e, 'Errore durante la disattivazione'))
    }
  }

  return (
    <>
      <div className="d-flex gap-2 align-items-center mb-3 flex-wrap">
        <Button variant="filled" type="button" onClick={() => setEditing('new')}>Nuova spesa ricorrente</Button>
        <span className="md-typescale-body-small text-muted">Le scadenze future si generano da sole ogni giorno (orizzonte 90 giorni).</span>
      </div>
      {error && <Callout tone="error">{error}</Callout>}
      {message && <Callout tone="success">{message}</Callout>}
      <Card variant="outlined" padding="none">
        <DataTable variant="plain">
            <thead>
              <tr><th>Descrizione</th><th>Categoria</th><th>Centro</th><th className="text-end">Netto</th><th className="text-end">IVA</th><th>Frequenza</th><th>Dal</th><th>Al</th><th>Stato</th><th /></tr>
            </thead>
            <tbody>
              {items.length === 0 && <DataTableEmptyRow colSpan={10} title="Nessuna spesa ricorrente" />}
              {items.map(r => (
                <tr key={r.id} className={r.isActive ? '' : 'text-muted'}>
                  <td>{r.description}</td>
                  <td>{categoryName(r.expenseCategoryId)}</td>
                  <td>{centreName(r.costCentreId)}</td>
                  <td className="text-end text-nowrap md-num">{formatEur(r.netAmount)}</td>
                  <td className="text-end text-nowrap md-num">{formatEur(r.vatAmount)}</td>
                  <td>{r.frequency}{r.frequency === 'Personalizzata' ? ` (${r.intervalMonths} mesi)` : ''}, il {r.dayOfMonth}</td>
                  <td className="text-nowrap">{formatDate(r.startDate)}</td>
                  <td className="text-nowrap">{formatDate(r.endDate)}</td>
                  <td>{r.isActive ? <StatusPill tone="success">Attiva</StatusPill> : <StatusPill tone="neutral">Disattivata</StatusPill>}</td>
                  <td className="text-end">
                    <div className="d-flex gap-1 justify-content-end flex-wrap">
                      <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => setEditing(r)} />
                      {r.isActive && <Button variant="text" size="sm" onClick={() => void generate(r)}>Genera scadenze</Button>}
                      {r.isActive && <Button variant="text" size="sm" onClick={() => void deactivate(r)}>Disattiva</Button>}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </DataTable>
      </Card>
      {editing && (
        <RecurringModal
          existing={editing === 'new' ? null : editing}
          centres={centres}
          categories={categories}
          suppliers={suppliers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void load() }}
        />
      )}
    </>
  )
}

function RecurringModal({ existing, centres, categories, suppliers, onClose, onSaved }: {
  existing: RecurringExpense | null
  centres: CostCentre[]
  categories: CostCategory[]
  suppliers: SupplierOption[]
  onClose: () => void
  onSaved: () => void
}) {
  const [description, setDescription] = useState(existing?.description ?? '')
  const [categoryId, setCategoryId] = useState(existing ? String(existing.expenseCategoryId) : '')
  const [centreId, setCentreId] = useState(existing?.costCentreId ? String(existing.costCentreId) : '')
  const [supplierId, setSupplierId] = useState(existing?.supplierId ? String(existing.supplierId) : '')
  const [net, setNet] = useState(existing ? String(existing.netAmount) : '')
  const [vat, setVat] = useState(existing ? String(existing.vatAmount) : '0')
  const [frequency, setFrequency] = useState<string>(existing?.frequency ?? 'Mensile')
  const [interval, setIntervalMonths] = useState(existing?.intervalMonths ? String(existing.intervalMonths) : '')
  const [day, setDay] = useState(existing ? String(existing.dayOfMonth) : '1')
  const [startDate, setStartDate] = useState(existing?.startDate.slice(0, 10) ?? new Date().toISOString().slice(0, 10))
  const [endDate, setEndDate] = useState(existing?.endDate?.slice(0, 10) ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  async function save() {
    const netValue = Number(net.replace(',', '.'))
    const vatValue = Number(vat.replace(',', '.'))
    if (!description.trim()) { setError('La descrizione è obbligatoria'); return }
    if (!categoryId) { setError('La categoria è obbligatoria'); return }
    if (!Number.isFinite(netValue) || netValue < 0) { setError('Importo netto non valido'); return }
    if (!Number.isFinite(vatValue) || vatValue < 0) { setError('IVA non valida'); return }
    if (frequency === 'Personalizzata' && !interval) { setError('Indica ogni quanti mesi'); return }
    const body: RecurringExpenseBody = {
      description: description.trim(),
      expenseCategoryId: Number(categoryId),
      costCentreId: centreId ? Number(centreId) : undefined,
      supplierId: supplierId ? Number(supplierId) : undefined,
      netAmount: netValue,
      vatAmount: vatValue,
      frequency,
      intervalMonths: frequency === 'Personalizzata' ? Number(interval) : undefined,
      dayOfMonth: Number(day),
      startDate,
      endDate: endDate || undefined,
    }
    setSaving(true)
    setError('')
    try {
      if (existing) await recurringExpensesApi.update(existing.id, body)
      else await recurringExpensesApi.create(body)
      onSaved()
    } catch (e) {
      setError(errMsg(e, 'Errore durante il salvataggio'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title={existing ? 'Modifica spesa ricorrente' : 'Nuova spesa ricorrente'} onClose={onClose} onSave={() => void save()} saving={saving}>
      {error && <Callout tone="error">{error}</Callout>}
      <FormSection title="Spesa">
        <TextField className="md-span-full" label="Descrizione" value={description} onChange={e => setDescription(e.target.value)} />
        <Select label="Categoria" value={categoryId} onChange={e => setCategoryId(e.target.value)}>
          <option value="">— scegli —</option>
          {categories.filter(c => c.isActive).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
        <Select label="Centro di costo" value={centreId} onChange={e => setCentreId(e.target.value)}>
          <option value="">— nessuno —</option>
          {centres.filter(c => c.isActive).map(c => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
        </Select>
        <Select className="md-span-full" label={`Fornitore${suppliers.length === 0 ? ' (nessuno disponibile)' : ''}`} value={supplierId} onChange={e => setSupplierId(e.target.value)}>
          <option value="">— nessuno —</option>
          {suppliers.map(s => <option key={s.id} value={s.id}>{s.companyName}</option>)}
        </Select>
      </FormSection>
      <FormSection title="Importi">
        <TextField label="Importo netto (€)" inputMode="decimal" value={net} onChange={e => setNet(e.target.value)} />
        <TextField label="IVA (€)" inputMode="decimal" value={vat} onChange={e => setVat(e.target.value)} />
      </FormSection>
      <FormSection title="Ricorrenza">
        <Select label="Frequenza" value={frequency} onChange={e => setFrequency(e.target.value)}>
          {RECURRING_FREQUENCIES.map(f => <option key={f} value={f}>{f}</option>)}
        </Select>
        {frequency === 'Personalizzata' && (
          <TextField label="Ogni quanti mesi (1-60)" type="number" min={1} max={60} value={interval} onChange={e => setIntervalMonths(e.target.value)} />
        )}
        <TextField label="Giorno del mese (1-31)" type="number" min={1} max={31} value={day} onChange={e => setDay(e.target.value)} />
      </FormSection>
      <FormSection title="Periodo">
        <TextField label="Dal" type="date" value={startDate} onChange={e => setStartDate(e.target.value)} />
        <TextField label="Fino al (facoltativo)" type="date" value={endDate} onChange={e => setEndDate(e.target.value)} />
      </FormSection>
    </Modal>
  )
}

/* ── Centri di costo e piano gestionale ──────────────────────────────────────────────────────── */

function CentresTab({ centres, categories, departments, departmentName, onChanged }: {
  centres: CostCentre[]
  categories: CostCategory[]
  departments: { id: number; name: string }[]
  departmentName: (id?: number | null) => string
  onChanged: () => Promise<void>
}) {
  const [code, setCode] = useState('')
  const [name, setName] = useState('')
  const [departmentId, setDepartmentId] = useState('')
  const [error, setError] = useState('')
  const [catError, setCatError] = useState('')

  async function create() {
    setError('')
    if (!code.trim() || !name.trim()) { setError('Codice e nome sono obbligatori'); return }
    try {
      await costCentresApi.create({ code: code.trim(), name: name.trim(), departmentId: departmentId ? Number(departmentId) : undefined })
      setCode('')
      setName('')
      setDepartmentId('')
      await onChanged()
    } catch (e) {
      setError(errMsg(e, 'Errore durante il salvataggio'))
    }
  }

  async function toggle(c: CostCentre) {
    setError('')
    try {
      await costCentresApi.update(c.id, { isActive: !c.isActive })
      await onChanged()
    } catch (e) {
      setError(errMsg(e, 'Errore durante l\'aggiornamento'))
    }
  }

  async function setParent(cat: CostCategory, value: string) {
    setCatError('')
    try {
      await categoryHierarchyApi.setParent(cat.id, value ? Number(value) : null)
      await onChanged()
    } catch (e) {
      setCatError(errMsg(e, 'Errore durante lo spostamento'))
    }
  }

  return (
    <CardGrid min={360}>
        <Card variant="outlined" padding="none">
          <CardHeader title="Centri di costo" />
          <CardContent>
            <FormGrid columns={3} className="md-form-grid--end">
              <div>
                <TextField label="Codice" maxLength={30} value={code} onChange={e => setCode(e.target.value)} />
              </div>
              <div className="md-span-2">
                <TextField label="Nome" maxLength={100} value={name} onChange={e => setName(e.target.value)} />
              </div>
              <div className="md-span-2">
                <Select label="Settore (facoltativo)" value={departmentId} onChange={e => setDepartmentId(e.target.value)}>
                  <option value="">— nessuno —</option>
                  {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
                </Select>
              </div>
              <div>
                <Button variant="tonal" fullWidth type="button" onClick={() => void create()}>Aggiungi</Button>
              </div>
            </FormGrid>
            {error && <div className="text-danger md-typescale-body-small mt-2">{error}</div>}
          </CardContent>
          <DataTable variant="plain">
              <thead><tr><th>Codice</th><th>Nome</th><th>Settore</th><th /></tr></thead>
              <tbody>
                {centres.length === 0 && <DataTableEmptyRow colSpan={4} title="Nessun centro di costo" />}
                {centres.map(c => (
                  <tr key={c.id} className={c.isActive ? '' : 'text-muted'}>
                    <td>{c.code}</td>
                    <td>{c.name}</td>
                    <td>{c.departmentId ? departmentName(c.departmentId) : '—'}</td>
                    <td className="text-end">
                      <Button variant="text" size="sm" onClick={() => void toggle(c)}>{c.isActive ? 'Disattiva' : 'Riattiva'}</Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
        </Card>

        <Card variant="outlined" padding="none">
          <CardHeader title="Piano gestionale dei costi (categorie, massimo 3 livelli)" />
          {catError && <Callout tone="error" className="m-2">{catError}</Callout>}
          <DataTable variant="plain">
              <thead><tr><th>Categoria</th><th>Sotto</th></tr></thead>
              <tbody>
                {categories.map(cat => (
                  <tr key={cat.id} className={cat.isActive ? '' : 'text-muted'}>
                    <td>{cat.name}</td>
                    <td>
                      <Select
                        label={`Categoria padre di ${cat.name}`}
                        labelHidden
                        value={cat.parentId ?? ''}
                        onChange={e => void setParent(cat, e.target.value)}
                      >
                        <option value="">— radice —</option>
                        {categories.filter(o => o.id !== cat.id).map(o => <option key={o.id} value={o.id}>{o.name}</option>)}
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
        </Card>
    </CardGrid>
  )
}

/* ── Ripartizione dei costi comuni (F-121) ────────────────────────────────────────────────────── */

const STATUS_BADGE: Record<string, PillTone> = { Bozza: 'neutral', Attiva: 'success', Ritirata: 'neutral' }

const UNALLOCATED_REASONS: Record<string, string> = {
  NESSUNA_PRODUZIONE: 'Nessuna produzione nel mese',
  PRODUZIONE_NON_DISPONIBILE: 'Produzione non disponibile',
  NESSUN_ORGANICO: 'Nessun organico nei centri di destinazione',
}

function methodLabel(m: string): string {
  return ALLOCATION_METHODS.find(x => x.id === m)?.label ?? m
}

function quotaLabel(method: string, q: { percentage?: number | null; weight?: number | null }): string {
  if (method === 'manuale') return `${(q.percentage ?? 0).toLocaleString('it-IT', { maximumFractionDigits: 4 })}%`
  if (method === 'pesi') return `peso ${(q.weight ?? 0).toLocaleString('it-IT', { maximumFractionDigits: 4 })}`
  if (method === 'per_dipendenti') return 'da organico'
  return 'da produzione'
}

function AllocationTab({ centres, categories, centreName, writer }: {
  centres: CostCentre[]
  categories: CostCategory[]
  centreName: (id?: number | null) => string
  writer: boolean
}) {
  const confirm = useConfirm()
  const [rules, setRules] = useState<AllocationRule[]>([])
  const [error, setError] = useState('')
  const [editing, setEditing] = useState<AllocationRule | 'new' | null>(null)
  const [previewing, setPreviewing] = useState<AllocationRule | null>(null)
  const [busy, setBusy] = useState<number | null>(null)
  const [from, setFrom] = useState(monthStart())
  const [to, setTo] = useState(monthEnd())
  const [report, setReport] = useState<AllocationReport | null>(null)
  const [reportError, setReportError] = useState('')

  const loadRules = useCallback(async () => {
    try {
      setRules(await costAllocationApi.list())
      setError('')
    } catch (e) {
      setError(errMsg(e, 'Errore durante il caricamento delle regole'))
    }
  }, [])

  const loadReport = useCallback(async () => {
    try {
      setReport(await costAllocationApi.report(from || undefined, to || undefined))
      setReportError('')
    } catch (e) {
      setReport(null)
      setReportError(errMsg(e, 'Errore durante il calcolo della ripartizione'))
    }
  }, [from, to])

  useEffect(() => { void loadRules() }, [loadRules])
  useEffect(() => { void loadReport() }, [loadReport])

  const act = async (rule: AllocationRule, run: () => Promise<void>, fallback: string) => {
    setBusy(rule.id)
    setError('')
    try {
      await run()
      await Promise.all([loadRules(), loadReport()])
    } catch (e) {
      setError(errMsg(e, fallback))
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <Callout tone="info">
        Si ripartisce il <strong>netto</strong> (IVA esclusa). I pesi sono valori inseriti a mano (per esempio i dipendenti):
        non esiste un'anagrafica. Le quote sono calcolate alla lettura e non vengono scritte nelle spese.
      </Callout>

      <Card variant="outlined" padding="none">
        <CardHeader
          title="Regole di ripartizione"
          actions={writer ? (
            <Button variant="filled" type="button" onClick={() => setEditing('new')}>Nuova regola</Button>
          ) : undefined}
        />
        {error && <Callout tone="error" className="m-3 mb-0">{error}</Callout>}
        {rules.length === 0 ? (
          <EmptyState icon="inbox" title="Nessuna regola di ripartizione." />
        ) : (
          <DataTable variant="plain">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Fonte</th>
                  <th>Metodo</th>
                  <th>Destinazioni</th>
                  <th>Validità</th>
                  <th>Stato</th>
                  <th className="text-end">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {rules.map(r => (
                  <tr key={r.id}>
                    <td>
                      {r.name}
                      {r.expenseCategoryId ? <div className="md-typescale-body-small text-muted">solo {categories.find(c => c.id === r.expenseCategoryId)?.name ?? `#${r.expenseCategoryId}`}</div> : null}
                    </td>
                    <td>{centreName(r.sourceCostCentreId)}</td>
                    <td>{methodLabel(r.method)}</td>
                    <td className="md-typescale-body-small">
                      {r.targets.map(t => (
                        <div key={t.targetCostCentreId}>{centreName(t.targetCostCentreId)} · {quotaLabel(r.method, t)}</div>
                      ))}
                    </td>
                    <td className="text-nowrap md-typescale-body-small">{formatDate(r.validFrom)} – {r.validTo ? formatDate(r.validTo) : 'aperta'}</td>
                    <td><StatusPill tone={STATUS_BADGE[r.status] ?? 'neutral'}>{r.status}</StatusPill></td>
                    <td className="text-end text-nowrap">
                      <Button variant="text" size="sm" type="button" className="me-1" onClick={() => setPreviewing(r)}>Anteprima quote</Button>
                      {writer && r.status === 'Bozza' && (
                        <>
                          <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => setEditing(r)} />
                          <Button variant="text" size="sm"
                            type="button" className="me-1" disabled={busy === r.id}
                            onClick={() => void act(r, () => costAllocationApi.activate(r.id), 'Impossibile attivare la regola')}
                          >
                            Attiva
                          </Button>
                        </>
                      )}
                      {writer && r.status !== 'Ritirata' && (
                        <Button variant="text" size="sm"
                          type="button" disabled={busy === r.id}
                          onClick={async () => {
                            if (await confirm({ title: 'Ritira la regola', message: 'Il ritiro è definitivo. Continuare?', confirmLabel: 'Ritira', destructive: true })) {
                              void act(r, () => costAllocationApi.retire(r.id), 'Impossibile ritirare la regola')
                            }
                          }}
                        >
                          Ritira
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
        )}
      </Card>

      <FilterBar>
        <div>
          <TextField label="Dal" type="date" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div>
          <TextField label="Al" type="date" value={to} onChange={e => setTo(e.target.value)} />
        </div>
      </FilterBar>

      {reportError && <Callout tone="error">{reportError}</Callout>}
      {report && (
        <>
          <Card variant="outlined" padding="none">
            <CardHeader title={`Costi per centro dopo la ripartizione · ${formatDate(report.from)} – ${formatDate(report.to)} · netti`} />
            {report.costCentres.length === 0 ? (
              <EmptyState icon="inbox" title="Nessuna spesa con un centro di costo nel periodo." />
            ) : (
              <DataTable variant="plain">
                  <thead>
                    <tr>
                      <th>Centro</th>
                      <th className="text-end">Diretto</th>
                      <th className="text-end">Ricevuto</th>
                      <th className="text-end">Ceduto</th>
                      <th className="text-end">Totale</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.costCentres.map(c => (
                      <tr key={c.costCentreId}>
                        <td>{c.code} · {c.name}</td>
                        <td className="text-end text-nowrap md-num">{formatEur(c.direct)}</td>
                        <td className="text-end text-nowrap md-num">{formatEur(c.received)}</td>
                        <td className="text-end text-nowrap md-num">{formatEur(c.ceded)}</td>
                        <td className="text-end text-nowrap md-typescale-emphasized-body-medium md-num">{formatEur(c.total)}</td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
            )}
          </Card>

          {report.rules.length > 0 && (
            <Card variant="outlined" padding="none">
              <CardHeader title="Regole applicate, mese per mese" />
              <DataTable variant="plain">
                  <thead>
                    <tr>
                      <th>Regola</th>
                      <th>Mese</th>
                      <th className="text-end">Spese</th>
                      <th>Quote</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.rules.flatMap(r => r.months.map(m => (
                      <tr key={`${r.ruleId}-${m.year}-${m.month}`}>
                        <td>{r.name}<div className="md-typescale-body-small text-muted">{methodLabel(r.method)}</div></td>
                        <td>{String(m.month).padStart(2, '0')}/{m.year}</td>
                        <td className="text-end">{m.expensesCount}</td>
                        <td className="md-typescale-body-small">
                          {m.quotas.map(q => (
                            <div key={q.targetCostCentreId}>
                              {centreName(q.targetCostCentreId)} · quota {q.quota.toLocaleString('it-IT', { maximumFractionDigits: 4 })} · {formatEur(q.amount)}
                            </div>
                          ))}
                        </td>
                      </tr>
                    )))}
                  </tbody>
                </DataTable>
            </Card>
          )}

          {report.unallocated.length > 0 && (
            <Card variant="outlined" padding="none">
              <CardHeader title="Spese non ripartite" />
              <DataTable variant="plain">
                  <thead>
                    <tr>
                      <th>Spesa</th>
                      <th>Data</th>
                      <th>Centro</th>
                      <th className="text-end">Netto</th>
                      <th>Motivo</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.unallocated.map(u => (
                      <tr key={u.expenseId}>
                        <td>#{u.expenseId}</td>
                        <td>{formatDate(u.expenseDate)}</td>
                        <td>{centreName(u.costCentreId)}</td>
                        <td className="text-end text-nowrap md-num">{formatEur(u.netAmount)}</td>
                        <td>{UNALLOCATED_REASONS[u.reason] ?? u.reason}</td>
                      </tr>
                    ))}
                  </tbody>
                </DataTable>
            </Card>
          )}
        </>
      )}

      {editing && (
        <AllocationRuleModal
          existing={editing === 'new' ? null : editing}
          centres={centres}
          categories={categories}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void loadRules(); void loadReport() }}
        />
      )}
      {previewing && (
        <AllocationPreviewModal rule={previewing} centreName={centreName} onClose={() => setPreviewing(null)} />
      )}
    </>
  )
}

function AllocationRuleModal({ existing, centres, categories, onClose, onSaved }: {
  existing: AllocationRule | null
  centres: CostCentre[]
  categories: CostCategory[]
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(existing?.name ?? '')
  const [method, setMethod] = useState<AllocationMethod>(existing?.method ?? 'manuale')
  const [source, setSource] = useState(existing ? String(existing.sourceCostCentreId) : '')
  const [category, setCategory] = useState(existing?.expenseCategoryId ? String(existing.expenseCategoryId) : '')
  const [validFrom, setValidFrom] = useState(existing?.validFrom ?? monthStart())
  const [validTo, setValidTo] = useState(existing?.validTo ?? '')
  const [reason, setReason] = useState(existing?.changeReason ?? '')
  const [rows, setRows] = useState<{ target: string; value: string }[]>(
    existing?.targets.map(t => ({ target: String(t.targetCostCentreId), value: String(t.percentage ?? t.weight ?? '') }))
      ?? [{ target: '', value: '' }, { target: '', value: '' }],
  )
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const usesValue = method === 'manuale' || method === 'pesi'
  // R3-10: il metodo "per dipendenti" si offre solo con il flag acceso (o se la regola in modifica già lo usa).
  const dimsOn = useFinanceDimensions().enabled
  const total = rows.reduce((sum, r) => sum + (Number(r.value.replace(',', '.')) || 0), 0)

  const save = async () => {
    setError('')
    const targets: AllocationTarget[] = []
    for (const r of rows) {
      if (!r.target) { setError('Ogni destinazione richiede un centro di costo.'); return }
      const n = Number(r.value.replace(',', '.'))
      if (usesValue && (!Number.isFinite(n) || n <= 0)) { setError('Ogni destinazione richiede una quota maggiore di zero.'); return }
      targets.push({
        targetCostCentreId: Number(r.target),
        percentage: method === 'manuale' ? n : null,
        weight: method === 'pesi' ? n : null,
      })
    }
    if (!source) { setError('Scegli il centro fonte.'); return }
    const body: AllocationRuleBody = {
      name: name.trim(),
      method,
      sourceCostCentreId: Number(source),
      expenseCategoryId: category ? Number(category) : undefined,
      validFrom,
      validTo: validTo || undefined,
      changeReason: reason.trim() || undefined,
      targets,
    }
    setSaving(true)
    try {
      if (existing) await costAllocationApi.update(existing.id, body)
      else await costAllocationApi.create(body)
      onSaved()
    } catch (e) {
      setError(errMsg(e, 'Impossibile salvare la regola'))
    } finally {
      setSaving(false)
    }
  }

  const activeCentres = centres.filter(c => c.isActive)

  return (
    <Modal title={existing ? 'Modifica regola (bozza)' : 'Nuova regola di ripartizione'} onClose={onClose} onSave={() => void save()} saving={saving}>
      {error && <Callout tone="error">{error}</Callout>}
      <FormSection title="Regola">
        <TextField className="md-span-full" label="Nome" value={name} maxLength={100} onChange={e => setName(e.target.value)} />
        <div className="md-span-full">
          <Select label="Metodo" value={method} onChange={e => setMethod(e.target.value as AllocationMethod)}>
            {ALLOCATION_METHODS.filter(m => m.id !== 'per_dipendenti' || dimsOn || existing?.method === 'per_dipendenti')
              .map(m => <option key={m.id} value={m.id}>{m.label}</option>)}
          </Select>
          {method === 'per_dipendenti' && (
            <div className="md-typescale-body-small text-muted">La quota di ogni destinazione è l'organico del suo centro di costo (si imposta in «Dimensioni e piano dei conti»).</div>
          )}
          {(method === 'produzione_inserite' || method === 'produzione_attivate') && (
            <div className="md-typescale-body-small text-muted">Le destinazioni devono essere centri di costo con un settore: la quota è la produzione del mese della spesa.</div>
          )}
        </div>
        <Select label="Centro fonte" value={source} onChange={e => setSource(e.target.value)}>
          <option value="">—</option>
          {activeCentres.map(c => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
        </Select>
        <Select label="Solo categoria (facoltativo)" value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">Tutte</option>
          {categories.filter(c => c.isActive).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
      </FormSection>
      <FormSection title="Periodo">
        <TextField label="Valida dal" type="date" value={validFrom} onChange={e => setValidFrom(e.target.value)} />
        <TextField label="Fino al (facoltativo)" type="date" value={validTo} onChange={e => setValidTo(e.target.value)} />
      </FormSection>
      <FormSection title="Destinazioni" columns={1}>
        <div className="d-flex flex-column gap-2">
          {rows.map((r, i) => (
            <div className="d-flex flex-wrap gap-2" key={i}>
              <Select
                label="Centro di destinazione" labelHidden value={r.target}
                onChange={e => setRows(rows.map((x, j) => (j === i ? { ...x, target: e.target.value } : x)))}
              >
                <option value="">Centro di destinazione…</option>
                {activeCentres.filter(c => String(c.id) !== source).map(c => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
              </Select>
              {usesValue && (
                <TextField
                  label={method === 'manuale' ? 'Percentuale' : 'Peso'} labelHidden className="md-u-maxw-112" inputMode="decimal"
                  placeholder={method === 'manuale' ? '% ' : 'peso'} value={r.value}
                  onChange={e => setRows(rows.map((x, j) => (j === i ? { ...x, value: e.target.value } : x)))}
                />
              )}
              <Button variant="text" size="sm"
                type="button" aria-label="Rimuovi destinazione"
                disabled={rows.length <= 1} onClick={() => setRows(rows.filter((_, j) => j !== i))}
              >
                ×
              </Button>
            </div>
          ))}
        </div>
        <div className="d-flex flex-wrap justify-content-between align-items-center gap-2">
          <Button variant="text" size="sm" type="button" className="px-0" onClick={() => setRows([...rows, { target: '', value: '' }])}>
            Aggiungi destinazione
          </Button>
          {method === 'manuale' && (
            <span className={`md-typescale-body-small ${total === 100 ? 'text-success' : 'text-danger'}`}>
              Totale {total.toLocaleString('it-IT', { maximumFractionDigits: 4 })}% (deve essere 100%)
            </span>
          )}
        </div>
      </FormSection>
      <FormGrid columns={1}>
        <TextField label="Motivazione (facoltativa)" value={reason} maxLength={500} onChange={e => setReason(e.target.value)} />
      </FormGrid>
    </Modal>
  )
}

function AllocationPreviewModal({ rule, centreName, onClose }: {
  rule: AllocationRule
  centreName: (id?: number | null) => string
  onClose: () => void
}) {
  const [month, setMonth] = useState(monthStart().slice(0, 7))
  const [data, setData] = useState<AllocationPreview | null>(null)
  const [error, setError] = useState('')

  useEffect(() => {
    let cancelled = false
    costAllocationApi.preview(rule.id, month)
      .then(d => { if (!cancelled) { setData(d); setError('') } })
      .catch(e => { if (!cancelled) { setData(null); setError(errMsg(e, 'Impossibile calcolare le quote')) } })
    return () => { cancelled = true }
  }, [rule.id, month])

  return (
    <Modal
      title={`Quote del mese · ${rule.name}`}
      onClose={onClose}
      supportingText={
        data && !data.available
          ? 'I conteggi di produzione non sono disponibili.'
          : data && data.available && data.totalQuota <= 0
            ? 'Nessuna produzione nel mese: le spese non verrebbero ripartite.'
            : undefined
      }
    >
      {error && <Callout tone="error">{error}</Callout>}
      <FormGrid columns={1}>
        <TextField label="Mese" type="month" value={month} onChange={e => setMonth(e.target.value)} />
      </FormGrid>
      {data && (
        <div className="md-dialog__bleed">
          <DataTable variant="plain">
            <thead>
              <tr><th>Destinazione</th><th className="text-end">Quota</th><th className="text-end">%</th></tr>
            </thead>
            <tbody>
              {data.quotas.map(q => (
                <tr key={q.targetCostCentreId}>
                  <td>{centreName(q.targetCostCentreId)}</td>
                  <td className="text-end md-num">{q.quota.toLocaleString('it-IT', { maximumFractionDigits: 4 })}</td>
                  <td className="text-end md-num">{q.sharePct.toLocaleString('it-IT', { maximumFractionDigits: 2 })}%</td>
                </tr>
              ))}
            </tbody>
          </DataTable>
        </div>
      )}
    </Modal>
  )
}

/* ── Costi variabili generati dalla produzione (F-125) ──────────────────────────────────────── */

const SKIP_REASONS: Record<string, string> = {
  QUANTITA_ZERO: 'Nessuna pratica nel mese',
  MESE_CHIUSO: 'Mese contabile chiuso',
  GIA_GENERATA: 'Spesa già generata',
  PRODUZIONE_NON_DISPONIBILE: 'Produzione non disponibile',
}

function lastCompleteMonth(): string {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth() - 1, 1, 12).toISOString().slice(0, 7)
}

function monthsAgo(n: number): string {
  const d = new Date()
  return new Date(d.getFullYear(), d.getMonth() - n, 1, 12).toISOString().slice(0, 7)
}

function VariableCostTab({ centres, categories, departments, suppliers, centreName, categoryName, writer }: {
  centres: CostCentre[]
  categories: CostCategory[]
  departments: { id: number; name: string }[]
  suppliers: SupplierOption[]
  centreName: (id?: number | null) => string
  categoryName: (id?: number | null) => string
  writer: boolean
}) {
  const confirm = useConfirm()
  const [rules, setRules] = useState<VariableCostRule[]>([])
  const [error, setError] = useState('')
  const [editing, setEditing] = useState<VariableCostRule | 'new' | null>(null)
  const [monthRule, setMonthRule] = useState<VariableCostRule | null>(null)
  const [busy, setBusy] = useState<number | null>(null)
  const [from, setFrom] = useState(monthsAgo(6))
  const [to, setTo] = useState(lastCompleteMonth())
  const [report, setReport] = useState<VariableCostReport | null>(null)
  const [reportError, setReportError] = useState('')

  const departmentLabel = (id?: number | null) =>
    id ? departments.find(d => d.id === id)?.name ?? `Settore #${id}` : 'Tutti i settori'

  const loadRules = useCallback(async () => {
    try {
      setRules(await variableCostApi.list())
      setError('')
    } catch (e) {
      setError(errMsg(e, 'Errore durante il caricamento delle regole'))
    }
  }, [])

  const loadReport = useCallback(async () => {
    try {
      setReport(await variableCostApi.report(from ? `${from}-01` : undefined, to ? `${to}-01` : undefined))
      setReportError('')
    } catch (e) {
      setReport(null)
      setReportError(errMsg(e, 'Errore durante il calcolo del report'))
    }
  }, [from, to])

  useEffect(() => { void loadRules() }, [loadRules])
  useEffect(() => { void loadReport() }, [loadReport])

  const act = async (rule: VariableCostRule, run: () => Promise<void>, fallback: string) => {
    setBusy(rule.id)
    setError('')
    try {
      await run()
      await Promise.all([loadRules(), loadReport()])
    } catch (e) {
      setError(errMsg(e, fallback))
    } finally {
      setBusy(null)
    }
  }

  return (
    <>
      <Callout tone="info">
        Ogni pratica del mese genera un costo unitario, per <strong>settore</strong> (non per prodotto o operatore). La spesa
        Prevista si crea una sola volta per i mesi <strong>completi</strong> e non si aggiorna: le pratiche registrate dopo
        compaiono solo come differenza nel report. L'IVA è quella unitaria inserita nella regola. La generazione automatica è
        spenta finché non viene attivata sul server.
      </Callout>

      <Card variant="outlined" padding="none">
        <CardHeader
          title="Regole di costo variabile"
          actions={writer ? (
            <Button variant="filled" type="button" onClick={() => setEditing('new')}>Nuova regola</Button>
          ) : undefined}
        />
        {error && <Callout tone="error" className="m-3 mb-0">{error}</Callout>}
        {rules.length === 0 ? (
          <EmptyState icon="inbox" title="Nessuna regola di costo variabile." />
        ) : (
          <DataTable variant="plain">
              <thead>
                <tr>
                  <th>Nome</th>
                  <th>Base</th>
                  <th>Settore</th>
                  <th className="text-end">Unitario netto</th>
                  <th className="text-end">IVA unitaria</th>
                  <th>Categoria</th>
                  <th>Validità</th>
                  <th>Stato</th>
                  <th className="text-end">Azioni</th>
                </tr>
              </thead>
              <tbody>
                {rules.map(r => (
                  <tr key={r.id}>
                    <td>
                      {r.name}
                      {r.costCentreId ? <div className="md-typescale-body-small text-muted">{centreName(r.costCentreId)}</div> : null}
                    </td>
                    <td>{VARIABLE_COST_BASES.find(b => b.id === r.basis)?.label ?? r.basis}</td>
                    <td>{departmentLabel(r.departmentId)}</td>
                    <td className="text-end md-num">{formatEur(r.unitNetAmount)}</td>
                    <td className="text-end md-num">{formatEur(r.unitVatAmount)}</td>
                    <td>{categoryName(r.expenseCategoryId)}</td>
                    <td className="text-nowrap md-typescale-body-small">{formatDate(r.validFrom)} – {r.validTo ? formatDate(r.validTo) : 'aperta'}</td>
                    <td><StatusPill tone={STATUS_BADGE[r.status] ?? 'neutral'}>{r.status}</StatusPill></td>
                    <td className="text-end text-nowrap">
                      <Button variant="text" size="sm" type="button" className="me-1" onClick={() => setMonthRule(r)}>
                        {writer && r.status === 'Attiva' ? 'Anteprima / Genera mese' : 'Anteprima mese'}
                      </Button>
                      {writer && r.status === 'Bozza' && (
                        <>
                          <IconButton icon="edit" size="sm" aria-label="Modifica" onClick={() => setEditing(r)} />
                          <Button variant="text" size="sm"
                            type="button" className="me-1" disabled={busy === r.id}
                            onClick={() => void act(r, () => variableCostApi.activate(r.id), 'Impossibile attivare la regola')}
                          >
                            Attiva
                          </Button>
                        </>
                      )}
                      {writer && r.status !== 'Ritirata' && (
                        <Button variant="text" size="sm"
                          type="button" disabled={busy === r.id}
                          onClick={async () => {
                            if (await confirm({ title: 'Ritira la regola', message: 'Il ritiro è definitivo. Le spese già generate restano. Continuare?', confirmLabel: 'Ritira', destructive: true })) {
                              void act(r, () => variableCostApi.retire(r.id), 'Impossibile ritirare la regola')
                            }
                          }}
                        >
                          Ritira
                        </Button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </DataTable>
        )}
      </Card>

      <FilterBar>
        <div>
          <TextField label="Dal mese" type="month" value={from} onChange={e => setFrom(e.target.value)} />
        </div>
        <div>
          <TextField label="Al mese" type="month" value={to} max={lastCompleteMonth()} onChange={e => setTo(e.target.value)} />
        </div>
      </FilterBar>

      {reportError && <Callout tone="error">{reportError}</Callout>}
      {report && (
        <Card variant="outlined" padding="none">
          <CardHeader title="Costi variabili generati e pratiche di oggi · netti" />
          {!report.productionAvailable && (
            <Callout tone="warning" className="m-3 mb-0">I conteggi di produzione non sono disponibili.</Callout>
          )}
          {report.rows.length === 0 ? (
            <EmptyState icon="inbox" title="Nessuna regola attiva o ritirata nel periodo." />
          ) : (
            <DataTable variant="plain">
                <thead>
                  <tr>
                    <th>Regola</th>
                    <th>Mese</th>
                    <th className="text-end">Pratiche oggi</th>
                    <th className="text-end">Pratiche generate</th>
                    <th className="text-end">Differenza</th>
                    <th className="text-end">Netto generato</th>
                    <th>Spesa</th>
                  </tr>
                </thead>
                <tbody>
                  {report.rows.map(row => (
                    <tr key={`${row.ruleId}-${row.month}`}>
                      <td>{row.ruleName}<div className="md-typescale-body-small text-muted">{departmentLabel(row.departmentId)}</div></td>
                      <td>{row.month}</td>
                      <td className="text-end md-num">{row.currentQuantity ?? '—'}</td>
                      <td className="text-end md-num">{row.generatedQuantity ?? '—'}</td>
                      <td className={`text-end ${row.difference ? 'text-danger md-typescale-emphasized-body-medium' : ''}`}>
                        {row.difference === null || row.difference === undefined ? '—' : row.difference}
                      </td>
                      <td className="text-end md-num">{row.generatedNetAmount === null || row.generatedNetAmount === undefined ? '—' : formatEur(row.generatedNetAmount)}</td>
                      <td className="md-typescale-body-small">
                        {row.expenseId
                          ? <>#{row.expenseId} · {row.expenseStatus}</>
                          : <span className="text-muted">{row.skipReason ? SKIP_REASONS[row.skipReason] ?? row.skipReason : '—'}</span>}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </DataTable>
          )}
        </Card>
      )}

      {editing && (
        <VariableCostRuleModal
          existing={editing === 'new' ? null : editing}
          centres={centres}
          categories={categories}
          departments={departments}
          suppliers={suppliers}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); void loadRules() }}
        />
      )}
      {monthRule && (
        <VariableCostMonthModal
          rule={monthRule}
          writer={writer}
          onClose={() => setMonthRule(null)}
          onGenerated={() => void loadReport()}
        />
      )}
    </>
  )
}

function VariableCostRuleModal({ existing, centres, categories, departments, suppliers, onClose, onSaved }: {
  existing: VariableCostRule | null
  centres: CostCentre[]
  categories: CostCategory[]
  departments: { id: number; name: string }[]
  suppliers: SupplierOption[]
  onClose: () => void
  onSaved: () => void
}) {
  const [name, setName] = useState(existing?.name ?? '')
  const [basis, setBasis] = useState<VariableCostBasis>(existing?.basis ?? 'attivate')
  const [department, setDepartment] = useState(existing?.departmentId ? String(existing.departmentId) : '')
  const [net, setNet] = useState(existing ? String(existing.unitNetAmount) : '')
  const [vat, setVat] = useState(existing ? String(existing.unitVatAmount) : '0')
  const [category, setCategory] = useState(existing ? String(existing.expenseCategoryId) : '')
  const [centre, setCentre] = useState(existing?.costCentreId ? String(existing.costCentreId) : '')
  const [supplier, setSupplier] = useState(existing?.supplierId ? String(existing.supplierId) : '')
  const [validFrom, setValidFrom] = useState(existing?.validFrom ?? monthStart())
  const [validTo, setValidTo] = useState(existing?.validTo ?? '')
  const [reason, setReason] = useState(existing?.changeReason ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const save = async () => {
    setError('')
    const netN = Number(net.replace(',', '.'))
    const vatN = Number(vat.replace(',', '.'))
    if (!name.trim()) { setError('Il nome è obbligatorio.'); return }
    if (!Number.isFinite(netN) || netN <= 0) { setError('Il costo unitario netto deve essere maggiore di zero.'); return }
    if (!Number.isFinite(vatN) || vatN < 0) { setError('L\'IVA unitaria non può essere negativa.'); return }
    if (!category) { setError('Scegli la categoria di spesa.'); return }
    const body: VariableCostRuleBody = {
      name: name.trim(),
      basis,
      departmentId: department ? Number(department) : undefined,
      unitNetAmount: netN,
      unitVatAmount: vatN,
      expenseCategoryId: Number(category),
      costCentreId: centre ? Number(centre) : undefined,
      supplierId: supplier ? Number(supplier) : undefined,
      validFrom,
      validTo: validTo || undefined,
      changeReason: reason.trim() || undefined,
    }
    setSaving(true)
    try {
      if (existing) await variableCostApi.update(existing.id, body)
      else await variableCostApi.create(body)
      onSaved()
    } catch (e) {
      setError(errMsg(e, 'Impossibile salvare la regola'))
    } finally {
      setSaving(false)
    }
  }

  return (
    <Modal title={existing ? 'Modifica regola (bozza)' : 'Nuova regola di costo variabile'} onClose={onClose} onSave={() => void save()} saving={saving}>
      {error && <Callout tone="error">{error}</Callout>}
      <FormSection title="Regola">
        <TextField className="md-span-full" label="Nome" value={name} maxLength={100} onChange={e => setName(e.target.value)} />
        <Select label="Base di produzione" value={basis} onChange={e => setBasis(e.target.value as VariableCostBasis)}>
          {VARIABLE_COST_BASES.map(b => <option key={b.id} value={b.id}>{b.label}</option>)}
        </Select>
        <Select label="Settore" value={department} onChange={e => setDepartment(e.target.value)}>
          <option value="">Tutti i settori</option>
          {departments.map(d => <option key={d.id} value={d.id}>{d.name}</option>)}
        </Select>
      </FormSection>
      <FormSection title="Costo unitario">
        <TextField label="Costo unitario netto (€)" inputMode="decimal" value={net} onChange={e => setNet(e.target.value)} />
        <TextField label="IVA unitaria (€)" inputMode="decimal" value={vat} onChange={e => setVat(e.target.value)} />
      </FormSection>
      <FormSection title="Imputazione">
        <Select label="Categoria di spesa" value={category} onChange={e => setCategory(e.target.value)}>
          <option value="">—</option>
          {categories.filter(c => c.isActive).map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
        </Select>
        <Select label="Centro di costo (facoltativo)" value={centre} onChange={e => setCentre(e.target.value)}>
          <option value="">Nessuno</option>
          {centres.filter(c => c.isActive).map(c => <option key={c.id} value={c.id}>{c.code} · {c.name}</option>)}
        </Select>
        <Select className="md-span-full" label="Fornitore (facoltativo)" value={supplier} onChange={e => setSupplier(e.target.value)}>
          <option value="">Nessuno</option>
          {suppliers.map(s => <option key={s.id} value={s.id}>{s.companyName}</option>)}
        </Select>
      </FormSection>
      <FormSection title="Periodo">
        <TextField label="Valida dal (primo del mese)" type="date" value={validFrom} onChange={e => setValidFrom(e.target.value)} />
        <TextField label="Fino al (facoltativo)" type="date" value={validTo} onChange={e => setValidTo(e.target.value)} />
        <TextField className="md-span-full" label="Motivazione (facoltativa)" value={reason} maxLength={500} onChange={e => setReason(e.target.value)} />
      </FormSection>
    </Modal>
  )
}

function VariableCostMonthModal({ rule, writer, onClose, onGenerated }: {
  rule: VariableCostRule
  writer: boolean
  onClose: () => void
  onGenerated: () => void
}) {
  const [month, setMonth] = useState(lastCompleteMonth())
  const [data, setData] = useState<VariableCostMonth | null>(null)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')
  const [generating, setGenerating] = useState(false)
  const [version, setVersion] = useState(0)

  useEffect(() => {
    let cancelled = false
    variableCostApi.preview(rule.id, month)
      .then(d => { if (!cancelled) { setData(d); setError('') } })
      .catch(e => { if (!cancelled) { setData(null); setError(errMsg(e, 'Impossibile calcolare il mese')) } })
    return () => { cancelled = true }
  }, [rule.id, month, version])

  const generate = async () => {
    setGenerating(true)
    setError('')
    setMessage('')
    try {
      const r = await variableCostApi.generate(rule.id, month)
      setMessage(r.created ? `Spesa Prevista #${r.expenseId} creata.` : `Nessuna spesa creata: ${SKIP_REASONS[r.month.skipReason ?? ''] ?? 'già presente'}.`)
      setVersion(v => v + 1)
      onGenerated()
    } catch (e) {
      setError(errMsg(e, 'Impossibile generare il mese'))
    } finally {
      setGenerating(false)
    }
  }

  return (
    <Modal
      title={`Mese · ${rule.name}`}
      onClose={onClose}
      supportingText={data && !data.productionAvailable ? 'I conteggi di produzione non sono disponibili.' : undefined}
      {...(writer && rule.status === 'Attiva'
        ? { onSave: () => void generate(), saving: generating, saveLabel: 'Genera mese', savingLabel: 'Generazione…', saveDisabled: !data?.wouldGenerate }
        : {})}
    >
      {error && <Callout tone="error">{error}</Callout>}
      {message && <Callout tone="success">{message}</Callout>}
      <FormGrid columns={1}>
        <TextField label="Mese" type="month" value={month} max={lastCompleteMonth()} onChange={e => setMonth(e.target.value)} />
      </FormGrid>
      {data && (
        <div className="md-dialog__bleed">
          <DataTable variant="plain">
            <tbody>
              <tr><th>Pratiche</th><td className="text-end md-num">{data.quantity ?? '—'}</td></tr>
              <tr><th>Netto</th><td className="text-end md-num">{formatEur(data.netAmount)}</td></tr>
              <tr><th>IVA</th><td className="text-end md-num">{formatEur(data.vatAmount)}</td></tr>
              <tr>
                <th>Esito</th>
                <td className="text-end">
                  {data.skipReason ? SKIP_REASONS[data.skipReason] ?? data.skipReason : data.wouldGenerate ? 'Da generare' : '—'}
                </td>
              </tr>
            </tbody>
          </DataTable>
        </div>
      )}
    </Modal>
  )
}

/* ── Modale ──────────────────────────────────────────────────────────────────────────────────── */

/** Modale con azioni nella barra del Dialog. Con `onSave` e' un modulo (Invio = conferma); senza c'e' solo "Chiudi". */
function Modal({ title, onClose, onSave, saving, saveLabel = 'Salva', savingLabel = 'Salvataggio…', saveDisabled, supportingText, children }: {
  title: string
  supportingText?: ReactNode | undefined
  onClose: () => void
  onSave?: (() => void) | undefined
  saving?: boolean | undefined
  saveLabel?: string
  savingLabel?: string
  saveDisabled?: boolean | undefined
  children: ReactNode
}) {
  const isForm = onSave !== undefined
  return (
    <Dialog
      open
      onClose={onClose}
      title={title}
      supportingText={supportingText}
      {...(isForm ? { as: 'form' as const, onSubmit: (e: FormEvent<HTMLFormElement>) => { e.preventDefault(); onSave() } } : {})}
      fullscreenOnCompact
      actions={
        <>
          <Button variant="text" type="button" dismiss onClick={onClose}>{isForm ? 'Annulla' : 'Chiudi'}</Button>
          {isForm ? (
            <Button variant="text" type="submit" disabled={saving || saveDisabled}>
              {saving ? savingLabel : saveLabel}
            </Button>
          ) : null}
        </>
      }
    >
      {children}
    </Dialog>
  )
}
